import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "react-toastify";

import Asteriskicon from "../../assets/img/astroik.svg";
import CreateSurveyHero from "../../components/Ui/CreateSurveyHero";
import { SurveyService } from "../../service/saveSurvey";
import { isSurveyExpired } from "../../utils/surveyPresentation";
import {
  dummySurveySummary,
  formatPreviewDate,
  initialHashtagItems,
  initialQuestions,
} from "./EditSurveyShared";

const questionTypeLabelMap = {
  single_select: "Single Select",
  multi_select: "Multi Select",
  rating: "Rating",
  open_end: "Open End",
};

const genderLabelMap = {
  1: "Male",
  2: "Female",
  3: "Other",
};

const resolveMediaUrl = (path) => {
  if (!path) {
    return "";
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const formatApiDate = (value) => {
  if (!value) {
    return "";
  }

  return value.split("T")[0] || "";
};

const getAgeRangeLabel = (minAge, maxAge) => {
  if (!minAge || !maxAge) {
    return "";
  }

  if (Number(minAge) >= 60) {
    return "60+ Years";
  }

  return `${minAge} - ${maxAge} Years`;
};

const mapPreviewResponse = (data) => {
  const survey = data?.survey || {};
  console.log("Survey =>", survey);

  return {
    surveySummary: {
      surveyTitle: survey.survey_title || "",
      surveyDescription: survey.survey_description || "",
      surveyTargetResponses: survey.target_responses || "",
      surveyStartDate: formatApiDate(survey.start_date),
      surveyEndDate: formatApiDate(survey.end_date),

      gender: survey.gender
      ?.split(",")
      .map(id => genderLabelMap[id.trim()] || id)
      .join(", ") || "--",
      
      ageRange: getAgeRangeLabel(survey.min_age, survey.max_age),

      sector: survey.sector || "",
      industry: survey.industry || "",

      country: survey.country || "",
      state: survey.state || "",
    },
    questions: (data?.questions || []).map((question) => ({
      id: question.id,
      type: questionTypeLabelMap[question.question_type] || question.question_type,
      text: question.question_text || "",
      image: question.question_image
        ? { preview: resolveMediaUrl(question.question_image) }
        : null,
      options: (question.options || []).map((option) => ({
        id: option.id,
        text: option.option_text || "",
        image: option.option_image
          ? { preview: resolveMediaUrl(option.option_image) }
          : null,
      })),
    })),
    hashtagItems: (data?.tags || []).map((tag) =>
      tag.tag_name?.startsWith("#") ? tag.tag_name : `#${tag.tag_name}`
    ),
  };
};

const EditSurveyPreview = () => {
  const navigate = useNavigate();
   const location = useLocation();
  const { surveyId } = useParams();

   console.log("Preview location.state =>", location.state);

  const [surveySummary, setSurveySummary] = useState(dummySurveySummary);
  const [questions, setQuestions] = useState(initialQuestions);
  const [hashtagItems, setHashtagItems] = useState(initialHashtagItems);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingloder, setIsLoadingloder] = useState(false);

  const [sectorList, setSectorList] = useState([]);
  const [industryList, setIndustryList] = useState([]);

  useEffect(() => {
    let isActive = true;

    const loadPreview = async () => {
      try {
        const response = await SurveyService.getSurveyPreview(surveyId);
        console.log("Preview API Response =>", response.data);
        const previewData = mapPreviewResponse(response.data?.data);

        console.log("API Questions =>", response.data.data.questions);

        console.log(
          "API Options",
          response.data.data.questions[0].options
        );


        if (!isActive) return;
        console.log("Preview Data =>", previewData);
        setSurveySummary(previewData.surveySummary);
        setQuestions(previewData.questions);
        setHashtagItems(previewData.hashtagItems);
      } catch (error) {
        console.error("Preview API Error =>", error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isActive = false;
    };
  }, [surveyId]);

  const resolvedSurveyTitle =
    surveySummary.surveyTitle?.trim() || dummySurveySummary.surveyTitle;
  const resolvedSurveyDescription =
    surveySummary.surveyDescription?.trim() ||
    dummySurveySummary.surveyDescription;
  const resolvedTargetResponses =
    surveySummary.surveyTargetResponses ||
    dummySurveySummary.surveyTargetResponses;
  const resolvedGender = surveySummary.gender || dummySurveySummary.gender;
  const resolvedAgeRange = surveySummary.ageRange || "";

  // const resolvedSector = surveySummary.sector || "";
  const resolvedSector = (surveySummary.sector || "")
  .split(",")
  .map((item) => item.trim())
  .join(", ");

  // const resolvedIndustry = surveySummary.industry || "";
  const resolvedIndustry = (surveySummary.industry || "")
  .split(",")
  .map((item) => item.trim())
  .join(", ");

  // const resolvedCountry = surveySummary.country || "";
  const resolvedCountry = (surveySummary.country || "")
  .split(",")
  .map((item) => item.trim())
  .join(", ");

  // const resolvedState = surveySummary.state || "";
  const resolvedState = (surveySummary.state || "")
  .split(",")
  .map((item) => item.trim())
  .join(", ");

  // const handleBackToQuestions = () => {
  //   navigate(`/edit-survey/step-2/${surveyId}`, {
  //     state: {
  //       surveySummary,
  //       questions,
  //       hashtagItems,
  //     },
  //   });
  // };

  const handleBackToQuestions = () => {
    console.log("Back Questions =>", questions);
    
  navigate(`/edit-survey/step-2/${surveyId}`, {
    state: {
      isBack: true,

      // STEP-1 DATA
      formData: location.state?.formData,
      surveyImage: location.state?.surveyImage,
      surveyImageRemoved: location.state?.surveyImageRemoved,
      surveySummary: location.state?.surveySummary,

      // STEP-2 DATA
      questions,
      hashtagItems,
      isEdit: location.state?.isEdit,
      previewData: location.state?.previewData,
    },
  });
};


  const handlePublishSurvey = async () => {
    if (isLoading || isLoadingloder) return;
    if (isSurveyExpired(surveySummary.surveyEndDate)) {
      toast.error("Survey end date has passed. Update the end date before publishing.");
      return;
    }
    try {
      setIsLoadingloder(true);
      const response = await SurveyService.publishSurvey(surveyId);
      if (response.data?.success === false) {
        throw new Error(response.data.message || "Failed to publish survey.");
      }
      toast.success(response.data?.message || "Survey published successfully");

      navigate("/my-surveys", {
        state: {
          activeTab: "activeSurveys",
        },
      });

    } catch (error) {
      console.error("Publish API Error =>", error);
      toast.error(error.response?.data?.message || error.message || "Failed to publish survey.");
    } finally {
      setIsLoadingloder(false);
    }
  };

  return (
    <>
    
      {isLoadingloder && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex justify-content-center align-items-center"
            style={{ zIndex: 9999 }}>
          <div
            className="spinner-border text-danger"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
        
      <div className="dashboard-create-survey">
        <CreateSurveyHero
          currentStep={3}
          handleBackToQuestions={handleBackToQuestions}
        />

        <div className="row g-3">
          <div className="col-12">
            <div className="cs-card cs-form-card">
              <div className="cs-step-label">Step 3/3: Preview and Publish</div>
              <div className="cs-progress-track">
                <div className="cs-progress-fill step-3"></div>
              </div>

              <div className="cs-preview-heading">Survey Preview</div>
              <div className="cs-preview-subtext">
                This is how participants see your survey
              </div>

              <div className="row g-3">
                {isLoading && (
                  <div className="col-12">
                    <div className="cs-preview-card">Loading preview...</div>
                  </div>
                )}

                <div className="col-12">
                  <div className="cs-preview-card">
                    <h1 className="ft-16 tw-font-bold">{resolvedSurveyTitle}</h1>
                    <div
                      className="ft-14"
                      dangerouslySetInnerHTML={{
                        __html: resolvedSurveyDescription,
                      }}
                    />

                    <div className="row g-   mt-0">
                      <div className="col-6 col-md-4 col-lg-auto">
                        <div className="cs-summary-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">
                            Target Responses
                          </div>
                          <p className="fw-bold ft-13 text-black mb-0">
                            {resolvedTargetResponses}
                          </p>
                        </div>
                      </div>

                      <div className="col-6 col-md-4 col-lg-auto">
                        <div className="cs-summary-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">
                            Start Date
                          </div>
                          <p className="fw-bold ft-13 text-black mb-0">
                            {formatPreviewDate(surveySummary.surveyStartDate)}
                          </p>
                        </div>
                      </div>

                      <div className="col-6 col-md-4 col-lg-auto">
                        <div className="cs-summary-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">
                            End Date
                          </div>
                          <p className="fw-bold ft-13 text-black mb-0">
                            {formatPreviewDate(surveySummary.surveyEndDate)}
                          </p>
                        </div>
                      </div>

                      <div className="col-6 col-md-4 col-lg-auto">
                        <div className="cs-summary-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">
                            Gender
                          </div>
                          <p className="fw-bold ft-13 text-black mb-0">
                            {resolvedGender}
                          </p>
                        </div>
                      </div>

                      <div className="col-6 col-md-4 col-lg-auto">
                        <div className="cs-summary-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">
                            Age Range
                          </div>
                          <p className="fw-bold ft-13 text-black mb-0">
                            {resolvedAgeRange}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="cs-preview-card">
                    <div className="cs-label mb-4">Target Audience</div>
                    <div className="row g-3">
                      <div className="col-6 col-md-3">
                        <div className="cs-audience-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">Sector</div>
                          <p className="fw-bold ft-13 text-black">
                            {resolvedSector}
                          </p>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="cs-audience-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">
                            Industry
                          </div>
                          <p className="fw-bold ft-13 text-black">
                            {resolvedIndustry}
                          </p>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="cs-audience-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">
                            Country
                          </div>
                          <p className="fw-bold ft-13 text-black">
                            {resolvedCountry}
                          </p>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="cs-audience-item">
                          <div className="cs-helper fw-medium mt-0 mb-2">State</div>
                          <p className="fw-bold ft-13 text-black">
                            {resolvedState}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="cs-preview-card">
                    <div className="cs-label mb-4">Tags/Hashtags</div>
                    <div className="cs-tags-preview">
                      {hashtagItems.map((tag) => (
                        <div className="cs-tag-pill" key={tag}>
                          {tag}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  {questions.map((question, index) => (
                    <div
                      className="cs-question-preview mt-3 mt-md-0 mb-3"
                      key={`preview-${question.id}`}
                    >
                      <div className="cs-question-number">
                        {index + 1}. {question.type}
                      </div>

                      <div className="cs-label mb-4 d-block">
                        {question.text || "Untitled Question"}
                      </div>

                      <div className="row g-3 align-items-start">
                        <div className={question.image?.preview ? "col-12 col-md" : "col-12"}>
                          {question.type === "Single Select" ? (
                            <>
                              {question.options.map((option, optionIndex) => (
                                <div
                                  className="cs-preview-option-block"
                                  key={`preview-option-${question.id}-${option.id}`}
                                >
                                  <div className="cs-preview-option">
                                    <span className="cs-preview-radio"></span>
                                    <span>
                                      {option.text || `Option ${optionIndex + 1}`}
                                    </span>
                                  </div>
                                  {option.image?.preview && (
                                    <div className="cs-preview-option-image-wrap">
                                      <img
                                        src={option.image.preview}
                                        alt={option.text || `Option ${optionIndex + 1}`}
                                        className="cs-preview-option-image"
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          ) : question.type === "Rating" ? (
                            <div className="rating-preview d-flex gap-2">
                              {[1, 2, 3, 4, 5].map((num) => (
                                <span
                                  key={num}
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    border: "1px solid #ddd",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {num}
                                </span>
                              ))}
                            </div>
                          ) : question.type === "Multi Select" ? (
                            <>
                              {question.options.map((option, optionIndex) => (
                                <div
                                  className="cs-preview-option-block"
                                  key={`preview-option-${question.id}-${option.id}`}
                                >
                                  <div className="cs-preview-option">
                                    <span className="cs-check-square"></span>
                                    <span>
                                      {option.text || `Option ${optionIndex + 1}`}
                                    </span>
                                  </div>
                                  {option.image?.preview && (
                                    <div className="cs-preview-option-image-wrap">
                                      <img
                                        src={option.image.preview}
                                        alt={option.text || `Option ${optionIndex + 1}`}
                                        className="cs-preview-option-image"
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          ) : question.type === "Open End" ? (
                            <textarea
                              value=""
                              placeholder="Long answer text"
                              readOnly
                              className="cs-preview-answer"
                              style={{
                                minHeight: "88px",
                                height: "88px",
                                resize: "none",
                              }}
                            />
                          ) : (
                            <input
                              type="text"
                              value=""
                              placeholder="Short answer text"
                              readOnly
                              className="cs-preview-answer"
                            />
                          )}
                        </div>
                        {question.image?.preview && (
                          <div className="col-12 col-md-auto">
                            <img
                              src={question.image.preview}
                              alt={`Question ${index + 1}`}
                              className="cs-preview-media"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="col-12">
                  <div className="cs-warning-strip px-3 py-3 gap-3 mt-3 rounded-8 d-flex items-center">
                    <img src={Asteriskicon} alt="" />
                    <span
                      className="ft-14 inter fst-italic"
                      style={{ color: "#4F3410" }}
                    >
                      When you publish, <strong>2,000 coins</strong> will be
                      deducted from your balance.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="cs-card cs-actions-card">
              <div className="row g-4 justify-content-end">
                {/* <div className="col-12 col-sm-auto">
                  <button type="button" className="cs-draft-btn w-100">
                    Save as Draft
                  </button>
                </div> */}
                <div className="col-12 col-sm-auto">
                  <button
                    type="button"
                    className="cs-primary-btn d-flex align-items-center justify-content-center gap-2 w-100"
                    onClick={handlePublishSurvey}
                    disabled={isLoading || isLoadingloder}
                  >
                    <span>Publish Survey</span>
                    <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditSurveyPreview;
